<template>
    <section class="carousel-component">
        <!-- An arrow that can't go anywhere fades out rather than sitting there
             inert. `inert` also takes it out of the tab order and stops clicks
             landing on something invisible. -->
        <button
            class="arrow"
            :class="{ 'is-hidden': atStart }"
            :inert="atStart"
            @click="navigateTo(active - 1, 'smooth')"
            aria-label="Previous"
            v-if="items?.length > 3">
            <SvgsLeftArrow />
        </button>
        <button
            class="arrow"
            :class="{ 'is-hidden': atEnd }"
            :inert="atEnd"
            @click="navigateTo(active + 1, 'smooth')"
            aria-label="Next"
            v-if="items?.length > 3">
            <SvgsRightArrow />
        </button>
        <ul class="carousel-inner" ref="carouselWrapperRef" :style="{ '--padding-right': padding }" id="carousel-inner">
            <li
                v-for="(item, index) in items"
                :key="item"
                :class="{
                    'is-full-width': fullWidth,
                    'active-item': active === index && items.length > 3,
                }"
                class="carousel-item"
                ref="slideRef"
                :data-num="index">
                <slot name="item" v-bind="item" />
            </li>
            <li class="carousel-item cheat-right" v-if="!isMobile"></li>
        </ul>
    </section>
</template>

<script setup>
    const emit = defineEmits(['updateParent']);

    const props = defineProps({
        items: {
            type: [Array, Object],
            required: false,
        },
        padding: {
            type: String,
            required: false,
            default: '0',
        },
        fullWidth: {
            type: Boolean,
            required: false,
            default: true,
        },
        scrollBehavior: {
            type: String,
            required: false,
            default: 'smooth',
        },
        trackIndex: {
            type: Boolean,
            required: false,
            default: false,
        },
        highlightActive: {
            type: Boolean,
            required: false,
            default: true,
        },
    });

    // Variables
    const active = ref(0);
    const slideRef = ref([]);
    const carouselWrapperRef = ref(null);
    const observer = ref(null);
    const isMobile = ref(true);
    const parentNotify = ref(null);

    // Where a click-driven smooth scroll is heading, while it is still in
    // flight. Without this the scroll listener drags `active` back to whatever
    // card the strip is passing over right now, so a second click computes
    // active + 1 from a stale index and both clicks land on the same card —
    // which is a good part of what the old 250ms click-swallow was concealing.
    const scrollingTo = ref(null);
    const scrollSettleTimer = ref(null);

    // Whether the strip is scrolled hard against either end, so the arrow that
    // would do nothing can be hidden. Read from scroll position rather than the
    // `active` index: `active` only advances on arrow clicks at desktop, so it
    // goes stale the moment someone scrolls or swipes instead.
    const atStart = ref(true);
    const atEnd = ref(false);

    function updateEdges() {
        const el = carouselWrapperRef.value;
        if (!el) return;
        // 1px of tolerance: fractional scroll offsets mean scrollLeft rarely
        // lands exactly on 0 or on the maximum.
        atStart.value = el.scrollLeft <= 1;
        atEnd.value = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;
    }

    /**
     * Keeps the highlight on the card the strip is actually scrolled to.
     *
     * Desktop only: on mobile the IntersectionObserver above already owns
     * `active`, and it drives the map pairing, so it is left alone. At desktop
     * the observer deliberately skipped this, which meant `active` only ever
     * moved when an arrow was clicked — scroll or swipe and the highlight stayed
     * on the first card (Asana O2-6).
     *
     * The leftmost card wins, matching `scroll-snap-align: start`.
     */
    function updateActiveFromScroll() {
        const el = carouselWrapperRef.value;
        if (!el || isMobile.value) return;

        if (scrollingTo.value !== null) {
            const slide = slideRef.value[scrollingTo.value];
            // Arrived, or as close as it will get: the last cards can't reach
            // their own offsetLeft because the strip runs out of scroll first,
            // so without the maxScroll test this would never release.
            const maxScroll = el.scrollWidth - el.clientWidth;
            const arrived = !slide || Math.abs(slide.offsetLeft - el.scrollLeft) <= 2 || el.scrollLeft >= maxScroll - 2;
            if (arrived) {
                clearTimeout(scrollSettleTimer.value);
                scrollingTo.value = null;
            }
            // Either way `active` is already the target, so leave it alone.
            return;
        }

        const slides = slideRef.value || [];
        let nearest = active.value;
        let smallestDistance = Infinity;

        slides.forEach((slide, i) => {
            if (!slide || i >= (props.items?.length ?? 0)) return;
            const distance = Math.abs(slide.offsetLeft - el.scrollLeft);
            if (distance < smallestDistance) {
                smallestDistance = distance;
                nearest = i;
            }
        });

        if (nearest !== active.value) active.value = nearest;
    }

    // Tell the parent where the strip ended up, once the clicking stops. Cancels
    // and reschedules rather than firing per change, so paging through five
    // cards zooms the map once at the destination instead of five times on the
    // way there.
    watch(active, () => {
        clearTimeout(parentNotify.value);
        parentNotify.value = setTimeout(() => {
            parentNotify.value = null;
            updateParent(active.value);
        }, 200);
    });

    const isSavedSite = ref(false);
    function setUpIntersectionObserver() {
        const options = {
            root: carouselWrapperRef.value,
            threshold: 0.9,
        };

        observer.value = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const current = parseInt(entry.target.getAttribute('data-num'), 10);
                if (entry.isIntersecting) {
                    if (isMobile.value) {
                        active.value = current;
                    }
                }
            });
        }, options);

        slideRef.value.forEach((item) => {
            observer.value.observe(item);
        });
    }

    /**
     * Moves the strip, on the click rather than 250ms after it.
     *
     * This used to schedule the scroll behind a 250ms timer and drop every click
     * that arrived while that timer ran — not queue them, drop them. So the
     * arrow did nothing for a quarter second, well past the point a control
     * reads as broken, and the clicks that impatience produced were exactly the
     * ones thrown away. Measured on production: five clicks 120ms apart advanced
     * two cards, and the first two moved nothing at all. From the day that
     * shipped, this component went from ~6 rageclicking people a day to 93 — the
     * largest single source on the site.
     *
     * Nothing needed the swallow. Both parents already debounce on their own
     * side (svg-map-with-carousel's handleIndexChange at 50ms, map.vue's
     * handleEvent at 250ms before it zooms), so the map was never relying on
     * this. What does need debouncing is the outbound updateParent notice, and
     * that lives on the `active` watcher above, where it cancels and reschedules
     * instead of discarding input.
     */
    function navigateTo(i, b) {
        const total = props.items?.length ?? 0;
        if (!total) return;

        // Clamp rather than index past the end: slideRef.value[total] is
        // undefined, the optional chain below swallows it, and the arrow reads
        // as broken again. The arrows are inert at both ends, but the map calls
        // this too, so the bound belongs here.
        const target = Math.min(Math.max(i, 0), total - 1);

        slideRef.value[target]?.scrollIntoView({
            behavior: b ? b : 'smooth',
            block: 'nearest',
            inline: 'start',
        });

        // Synchronously, so a second click computes active + 1 from where this
        // one is heading rather than from wherever the strip has smooth-scrolled
        // to so far. Desktop only: the arrows are display:none below $tablet and
        // the IntersectionObserver owns `active` on mobile, where it also drives
        // the map pairing.
        if (!isMobile.value) {
            active.value = target;
            scrollingTo.value = target;
            clearTimeout(scrollSettleTimer.value);
            // Safety net: if the strip never arrives — the user grabs it
            // mid-flight, or it was already as far as it goes — stop ignoring
            // scroll rather than freezing `active` on a target we never reached.
            scrollSettleTimer.value = setTimeout(() => {
                scrollingTo.value = null;
                updateActiveFromScroll();
            }, 1000);
        }

        nextTick(() => {
            if (isSavedSite.value) {
                window.siteQuery = null;
            }
        });
    }

    function updateParent(val) {
        emit('updateParent', val);
    }

    function checkIsMobile() {
        isMobile.value = window.innerWidth > 768 ? false : true;
    }

    defineExpose({
        navigateTo,
    });

    const { $listen } = useNuxtApp();

    function handleEdgeRecalc() {
        updateEdges();
        updateActiveFromScroll();
    }

    onMounted(() => {
        if (import.meta.client) {
            checkIsMobile();
            window.addEventListener('resize', checkIsMobile);
            window.addEventListener('resize', handleEdgeRecalc);
            nextTick(() => {
                // Passive: this only reads scroll offsets, so it must never
                // block scrolling itself.
                carouselWrapperRef.value?.addEventListener('scroll', handleEdgeRecalc, { passive: true });
                updateEdges();
            });
            nextTick(() => {
                $listen('returnToPosition', () => {
                    if (window?.siteQuery?.savedSite) {
                        isSavedSite.value = true;
                        let carouselIndex = props.items.findIndex((x) => x.id === window.siteQuery.savedSite);
                        setTimeout(() => {
                            navigateTo(carouselIndex, 'instant');
                        }, 800);
                    }
                });
                setTimeout(() => {
                    setUpIntersectionObserver();
                }, 500);
            });
        }
    });

    onBeforeUnmount(() => {
        if (import.meta.client) {
            if (observer.value) {
                observer.value.disconnect();
            }
            // Detach before the ref is cleared below, or the listener outlives
            // the component.
            carouselWrapperRef.value?.removeEventListener('scroll', handleEdgeRecalc);
            clearTimeout(parentNotify.value);
            clearTimeout(scrollSettleTimer.value);
            scrollingTo.value = null;
            active.value = 0;
            slideRef.value = [];
            carouselWrapperRef.value = null;
            window.removeEventListener('resize', checkIsMobile);
            window.removeEventListener('resize', handleEdgeRecalc);
        }
    });
</script>

<style lang="scss" scoped>
    .carousel-component {
        position: relative;

        @media (min-width: $tablet) {
            pointer-events: none;
        }

        .carousel-inner {
            display: flex;
            flex-wrap: nowrap;
            overflow-x: scroll;
            overflow-y: hidden;
            -webkit-overflow-scrolling: touch;
            scroll-snap-type: x mandatory;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
            -ms-overflow-style: none;
            scrollbar-width: none;
            align-items: flex-start;
            padding-right: var(--padding-right);
            gap: 16px;
            // margin-left: 7px;

            @media (min-width: $tablet) {
                gap: 8px;
                margin-left: 0px;
            }

            &::-webkit-scrollbar {
                display: none;
            }

            .carousel-item {
                scroll-snap-align: start;
                gap: 8px;
                display: flex;
                flex-direction: column;
                align-self: stretch;

                @media (min-width: $tablet) {
                    pointer-events: auto;
                    align-self: stretch;
                }

                &.is-full-width {
                    flex: 0 0 100%;

                    @media (min-width: $tablet) {
                        flex: 0 0 calc(39% + 8px);
                        pointer-events: auto;
                    }
                }

                &.cheat-right {
                    flex: 0 0 calc(60% - 10px);
                    pointer-events: none;
                }
            }
        }

        .button-wrapper {
            display: none;
            justify-content: space-between;
            margin: 17px;
        }

        .arrow {
            display: none;
            background: white;
            padding: 10px;
            border-radius: 50%;
            position: absolute;
            top: -50px;
            left: 10px;
            // The arrow is positioned but had no z-index, so it stacked purely
            // by DOM order and the cards — which come after it — painted over
            // it. It still LOOKED fine, because the card is transparent where
            // they overlap; only the clicks went to the card. Measured on the
            // Explore Like a Local carousel at 1384px: 72 of 81 points across
            // the arrow hit a card link instead, leaving just the centre live
            // (Asana O2-83).
            //
            // 2, not 1. Card internals that have to clear their own absolutely
            // positioned photo already sit at 1 — Cards/site.vue's
            // .card-site-label is the one that showed it, the SATURDAY/SUNDAY
            // ONLY tag. At a tie the later element in the DOM wins, and the
            // cards come after the arrow, so which of the two painted on top
            // depended on how far the strip happened to be scrolled.
            //
            // Still deliberately low rather than one of the $zStep vars: this
            // only has to beat the cards beside it, and anything higher would
            // start competing with the header and modals for no reason.
            z-index: 2;

            // Fades out at whichever end it can no longer scroll towards.
            // visibility rather than display so the fade actually plays, and so
            // a hidden arrow can't be clicked mid-transition.
            opacity: 1;
            visibility: visible;
            transition: opacity 0.2s ease, visibility 0s linear 0s, background-color 0.2s ease;

            // The arrows sit on a white circle over the cards, which gives no
            // sign they are pressable until you hit one. Same treatment as the
            // map's Download PDF button: fill with teal and flip the mark to
            // white.
            //
            // The stroke has to be overridden in CSS because left-arrow.vue and
            // right-arrow.vue hardcode stroke="black" as an attribute; a CSS
            // rule outranks a presentation attribute, so this wins without
            // touching either SVG.
            //
            // :deep() is required. This block is scoped, and the paths belong to
            // the SvgsLeftArrow/SvgsRightArrow child components, which carry
            // their own scope id — so a plain `svg path` compiles to
            // `path[data-v-<carousel>]` and matches nothing. Without it the
            // background turned teal and the arrow stayed black.
            :deep(svg path) {
                transition: stroke 0.2s ease;
            }

            // Pointer devices get the white circle and fill it on hover.
            @media (hover: hover) {
                &:hover {
                    background-color: $turquoise;

                    :deep(svg path) {
                        stroke: white;
                    }
                }
            }

            // Touch has no hover, so the resting state has to do that work. A
            // white circle sitting over a white card is exactly where these are
            // hardest to pick out, and on a phone there is no way to reveal it —
            // so on touch they wear the hover treatment permanently.
            @media (hover: none) {
                background-color: $turquoise;

                :deep(svg path) {
                    stroke: white;
                }
            }

            &.is-hidden {
                opacity: 0;
                visibility: hidden;
                // Delay the visibility flip until the fade has finished.
                transition: opacity 0.2s ease, visibility 0s linear 0.2s;
            }

            @media (prefers-reduced-motion: reduce) {
                transition: none;

                &.is-hidden {
                    transition: none;
                }
            }

            svg {
                width: 22px;
            }
        }

        .arrow:last-of-type {
            right: 10px;
            left: unset;
        }

        @media (min-width: $tablet) {
            .arrow {
                display: block;
                pointer-events: auto;
            }
        }
    }
</style>
